import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { AlertTriangle, LogIn, UserPlus } from "lucide-react";
import { toast } from "sonner";

const Login = () => {
  const [tab, setTab] = useState("login");
  const [loading, setLoading] = useState(false);
  const { login, register } = useAuth();
  const navigate = useNavigate();

  const [loginData, setLoginData] = useState({ email: "", password: "" });
  const [registerData, setRegisterData] = useState({
    email: "",
    password: "",
    name: "",
    role: "",
    department: "",
  });

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await login(loginData.email, loginData.password);
      toast.success("Welcome to ORBIT");
      navigate("/dashboard");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Login failed");
    } finally {
      setLoading(false);
    }
  };

  const handleRegister = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const data = {
        ...registerData,
        department: registerData.department || null,
      };
      await register(data);
      toast.success("Account created successfully");
      navigate("/dashboard");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Registration failed");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-background flex items-center justify-center p-4 relative overflow-hidden">
      {/* Background Pattern */}
      <div className="absolute inset-0 grid-pattern opacity-30" />
      
      {/* Gradient Overlay */}
      <div className="absolute inset-0 bg-gradient-to-br from-background via-background to-primary/5" />
      
      <div className="relative z-10 w-full max-w-md">
        {/* Logo */}
        <div className="text-center mb-8">
          <div className="inline-flex items-center justify-center w-16 h-16 bg-primary/20 rounded-sm mb-4 gold-glow">
            <AlertTriangle className="w-10 h-10 text-primary" />
          </div>
          <h1 className="font-heading text-4xl font-bold tracking-tight text-primary uppercase gold-glow-text">
            ORBIT
          </h1>
          <p className="text-muted-foreground text-sm tracking-widest uppercase mt-1">
            Operational Risk & Business Intelligence Tracker
          </p>
        </div>

        <Card className="orbit-card border-border/50 backdrop-blur-sm">
          <CardHeader className="text-center pb-4">
            <CardTitle className="font-heading text-lg uppercase tracking-wide">
              Access Control
            </CardTitle>
            <CardDescription className="text-xs">
              Golden Tulip Holland Resort Batu
            </CardDescription>
          </CardHeader>
          <CardContent>
            <Tabs value={tab} onValueChange={setTab} className="w-full">
              <TabsList className="grid w-full grid-cols-2 mb-6 bg-surface">
                <TabsTrigger 
                  value="login" 
                  className="font-heading uppercase text-xs tracking-wider data-[state=active]:bg-primary data-[state=active]:text-primary-foreground"
                  data-testid="login-tab"
                >
                  Login
                </TabsTrigger>
                <TabsTrigger 
                  value="register" 
                  className="font-heading uppercase text-xs tracking-wider data-[state=active]:bg-primary data-[state=active]:text-primary-foreground"
                  data-testid="register-tab"
                >
                  Register
                </TabsTrigger>
              </TabsList>

              <TabsContent value="login">
                <form onSubmit={handleLogin} className="space-y-4">
                  <div className="space-y-2">
                    <Label htmlFor="login-email" className="text-xs uppercase tracking-wider text-muted-foreground">
                      Email
                    </Label>
                    <Input
                      id="login-email"
                      type="email"
                      value={loginData.email}
                      onChange={(e) => setLoginData({ ...loginData, email: e.target.value })}
                      placeholder="your@email.com"
                      required
                      className="bg-background border-border focus:border-primary"
                      data-testid="login-email"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="login-password" className="text-xs uppercase tracking-wider text-muted-foreground">
                      Password
                    </Label>
                    <Input
                      id="login-password"
                      type="password"
                      value={loginData.password}
                      onChange={(e) => setLoginData({ ...loginData, password: e.target.value })}
                      placeholder="••••••••"
                      required
                      className="bg-background border-border focus:border-primary"
                      data-testid="login-password"
                    />
                  </div>
                  <Button
                    type="submit"
                    disabled={loading}
                    className="w-full btn-primary"
                    data-testid="login-submit"
                  >
                    <LogIn size={16} className="mr-2" />
                    {loading ? "Authenticating..." : "Access System"}
                  </Button>
                </form>
              </TabsContent>

              <TabsContent value="register">
                <form onSubmit={handleRegister} className="space-y-4">
                  <div className="space-y-2">
                    <Label className="text-xs uppercase tracking-wider text-muted-foreground">
                      Full Name
                    </Label>
                    <Input
                      value={registerData.name}
                      onChange={(e) => setRegisterData({ ...registerData, name: e.target.value })}
                      placeholder="John Doe"
                      required
                      className="bg-background border-border focus:border-primary"
                      data-testid="register-name"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label className="text-xs uppercase tracking-wider text-muted-foreground">
                      Email
                    </Label>
                    <Input
                      type="email"
                      value={registerData.email}
                      onChange={(e) => setRegisterData({ ...registerData, email: e.target.value })}
                      placeholder="your@email.com"
                      required
                      className="bg-background border-border focus:border-primary"
                      data-testid="register-email"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label className="text-xs uppercase tracking-wider text-muted-foreground">
                      Password
                    </Label>
                    <Input
                      type="password"
                      value={registerData.password}
                      onChange={(e) => setRegisterData({ ...registerData, password: e.target.value })}
                      placeholder="••••••••"
                      required
                      className="bg-background border-border focus:border-primary"
                      data-testid="register-password"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label className="text-xs uppercase tracking-wider text-muted-foreground">
                      Role
                    </Label>
                    <Select
                      value={registerData.role}
                      onValueChange={(value) => setRegisterData({ ...registerData, role: value })}
                      required
                    >
                      <SelectTrigger className="bg-background border-border" data-testid="register-role">
                        <SelectValue placeholder="Select role" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="FO">Front Office (FO)</SelectItem>
                        <SelectItem value="HOD">Head of Department (HOD)</SelectItem>
                        <SelectItem value="DM">Duty Manager (DM)</SelectItem>
                        <SelectItem value="GM">General Manager (GM)</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  {registerData.role === "HOD" && (
                    <div className="space-y-2">
                      <Label className="text-xs uppercase tracking-wider text-muted-foreground">
                        Department
                      </Label>
                      <Select
                        value={registerData.department}
                        onValueChange={(value) => setRegisterData({ ...registerData, department: value })}
                      >
                        <SelectTrigger className="bg-background border-border" data-testid="register-department">
                          <SelectValue placeholder="Select department" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="ENG">Engineering</SelectItem>
                          <SelectItem value="HK">Housekeeping</SelectItem>
                          <SelectItem value="FNB">F&B</SelectItem>
                          <SelectItem value="FO">Front Office</SelectItem>
                          <SelectItem value="SEC">Security</SelectItem>
                          <SelectItem value="IT">IT</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  )}
                  <Button
                    type="submit"
                    disabled={loading || !registerData.role}
                    className="w-full btn-primary"
                    data-testid="register-submit"
                  >
                    <UserPlus size={16} className="mr-2" />
                    {loading ? "Creating Account..." : "Create Account"}
                  </Button>
                </form>
              </TabsContent>
            </Tabs>
          </CardContent>
        </Card>

        <p className="text-center text-xs text-muted-foreground mt-6">
          © 2024 Golden Tulip Holland Resort Batu
        </p>
      </div>
    </div>
  );
};

export default Login;
